/**
 * ErrorStates Component Library
 *
 * Pre-built error state components for common error scenarios.
 * Provides user-friendly error messages with appropriate actions.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BouncyButton } from '@/components/AnimatedElements';
import { ScreenError, ErrorStateConfig } from '@/types/screen.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useThemeColors } from '@/styles';

export interface ErrorStateProps {
  /** Error object */
  error: ScreenError;
  /** Retry function */
  onRetry?: () => void;
  /** Configuration overrides */
  config?: ErrorStateConfig;
}

/**
 * Default error state component
 */
export const DefaultErrorState: React.FC<ErrorStateProps> = ({
  error,
  onRetry,
  config,
}) => {
  const colors = useThemeColors();
  const showRetry = config?.showRetry !== false && error.canRetry && onRetry;
  const title = config?.title || getErrorTitle(error.code);
  const description = config?.description || error.message;

  return (
    <div
      className="flex items-center justify-center p-4"
      style={{ minHeight: 'calc(400px * var(--sizing-multiplier, 1))' }}
    >
      <Card
        className="max-w-md w-full shadow-lg"
        style={{ borderColor: colors.error + '40' }}
      >
        <CardContent className="p-8 text-center space-y-4">
          {/* Error icon */}
          <div
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.error + '20' }}
          >
            {getErrorIcon(error.code, colors.error)}
          </div>

          {/* Title */}
          <h3
            className="text-xl font-semibold"
            style={{ color: colors.text.primary }}
          >
            {title}
          </h3>

          {/* Description */}
          <p style={{ color: colors.text.secondary }}>{description}</p>

          {/* Error details */}
          {error.details && (
            <div
              className="rounded-lg p-3 border"
              style={{
                backgroundColor: colors.error + '10',
                borderColor: colors.error + '40',
              }}
            >
              <p className="text-sm" style={{ color: colors.error }}>
                {error.details}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {showRetry && (
              <BouncyButton
                onClick={onRetry}
                className="flex-1 py-2 px-6 rounded-lg shadow-md transition-colors"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.text.inverse,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor =
                    colors.hover.background;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                }}
              >
                {config?.retryText || 'Try Again'}
              </BouncyButton>
            )}
            {config?.showSupport && (
              <BouncyButton
                onClick={() => {
                  // In a real app, this would open support chat or email
                  window.alert('Support contact functionality would go here');
                }}
                className="flex-1 py-2 px-6 rounded-lg shadow-md transition-colors"
                style={{
                  backgroundColor: colors.medium,
                  color: colors.text.inverse,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = colors.dark;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = colors.medium;
                }}
              >
                Contact Support
              </BouncyButton>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Network error state
 */
export const NetworkError: React.FC<{ onRetry?: () => void }> = ({
  onRetry,
}) => (
  <DefaultErrorState
    error={{
      code: ResponseCode.NETWORK_ERROR,
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
      canRetry: true,
      retryAction: onRetry,
    }}
    onRetry={onRetry}
    config={{
      title: 'Connection Problem',
      showRetry: true,
    }}
  />
);

/**
 * Authentication error state
 */
export const AuthenticationError: React.FC = () => (
  <DefaultErrorState
    error={{
      code: ResponseCode.UNAUTHORIZED,
      message: 'Your session has expired. Please log in again to continue.',
      canRetry: false,
    }}
    config={{
      title: 'Authentication Required',
      showRetry: false,
    }}
  />
);

/**
 * Permission error state
 */
export const PermissionError: React.FC = () => (
  <DefaultErrorState
    error={{
      code: ResponseCode.FORBIDDEN,
      message:
        'You do not have permission to access this resource. Please contact your administrator.',
      canRetry: false,
    }}
    config={{
      title: 'Access Denied',
      showRetry: false,
      showSupport: true,
    }}
  />
);

/**
 * Server error state
 */
export const ServerError: React.FC<{ onRetry?: () => void }> = ({
  onRetry,
}) => (
  <DefaultErrorState
    error={{
      code: ResponseCode.INTERNAL_ERROR,
      message:
        'The server encountered an error. Our team has been notified. Please try again later.',
      canRetry: true,
      retryAction: onRetry,
    }}
    onRetry={onRetry}
    config={{
      title: 'Server Error',
      showRetry: true,
      showSupport: true,
    }}
  />
);

/**
 * Not found error state
 */
export const NotFoundError: React.FC<{ resourceName?: string }> = ({
  resourceName = 'page',
}) => (
  <DefaultErrorState
    error={{
      code: ResponseCode.NOT_FOUND,
      message: `The ${resourceName} you're looking for doesn't exist or has been moved.`,
      canRetry: false,
    }}
    config={{
      title: 'Not Found',
      showRetry: false,
    }}
  />
);

/**
 * Validation error state
 */
export const ValidationError: React.FC<{ message: string }> = ({ message }) => (
  <DefaultErrorState
    error={{
      code: ResponseCode.VALIDATION_FAILED,
      message,
      canRetry: false,
    }}
    config={{
      title: 'Validation Error',
      showRetry: false,
    }}
  />
);

/**
 * Get error title based on response code
 */
function getErrorTitle(code: string): string {
  if (code.includes('NETWORK')) return 'Connection Problem';
  if (code.includes(ResponseCode.UNAUTHORIZED) || code.includes('401'))
    return 'Authentication Required';
  if (code.includes(ResponseCode.FORBIDDEN) || code.includes('403'))
    return 'Access Denied';
  if (code.includes('NOT_FOUND') || code.includes('404')) return 'Not Found';
  if (code.includes('TIMEOUT')) return 'Request Timeout';
  if (code.includes('500') || code.includes('INTERNAL')) return 'Server Error';
  if (code.includes('SERVICE_UNAVAILABLE') || code.includes('503'))
    return 'Service Unavailable';
  return 'Error';
}

/**
 * Get error icon based on response code
 */
function getErrorIcon(code: string, errorColor: string): React.ReactNode {
  if (code.includes('NETWORK')) {
    return (
      <svg
        className="w-10 h-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: errorColor }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
        />
      </svg>
    );
  }

  if (code.includes(ResponseCode.UNAUTHORIZED) || code.includes('401')) {
    return (
      <svg
        className="w-10 h-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: errorColor }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    );
  }

  if (code.includes(ResponseCode.FORBIDDEN) || code.includes('403')) {
    return (
      <svg
        className="w-10 h-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: errorColor }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
        />
      </svg>
    );
  }

  // Default error icon
  return (
    <svg
      className="w-10 h-10"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      style={{ color: errorColor }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
